<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <DspPartnerToolbar
        v-if="Object.keys(dsppartner).length !== 0"
        :dsppartner="dsppartner"
        closing
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>
              {{ $t('stats.source_stats') }}:
              <a
                v-if="getDomainNameById(sourceId)"
                href="#"
                class="text-decoration-none text-primary"
                @click.prevent="openWebsite(getDomainNameById(sourceId))"
              >
                <em>{{ getDomainNameById(sourceId) }}</em>
              </a>
              <em v-else>{{ getDomainNameById(sourceId) }}</em>
              <feather-icon
                v-if="getDomainNameById(sourceId)"
                icon="ExternalLinkIcon"
                size="16"
                class="ml-25 cursor-pointer text-primary"
                @click="openWebsite(getDomainNameById(sourceId))"
              />
              <span
                class="cursor-pointer copy-id ml-25"
                @click="copyToClipboard(sourceId)"
              >
                [ID: {{ sourceId }}]
              </span>
            </h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="isLoading"
              @refresh="fetchSourceDetails"
              @input="toggleRange"
            >
              <template #additional-filters>
                <b-dropdown
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="outline-primary"
                  right
                  toggle-class="d-flex align-items-center"
                  no-caret
                  size="sm"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                    />
                  </template>
                  <b-dropdown-item
                    v-if="sourceStatus === 'active'"
                    @click="blockSource"
                  >
                    <feather-icon
                      icon="XCircleIcon"
                      size="16"
                      class="mr-50"
                    />
                    <span>{{ $t('stats.block') }}</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-else
                    @click="unblockSource"
                  >
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="16"
                      class="mr-50"
                    />
                    <span>{{ $t('stats.unblock') }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </StatsDateRangeFilter>
          </div>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-table
        v-else-if="rows.length"
        :items="rows"
        :fields="fields"
        striped
        responsive
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        foot-clone
        class="text-center"
        @sort-changed="onSortChanged"
      >
        <template #head(date)>
          {{ $t('stats.date') }}
        </template>
        <template #head(impressions)>
          {{ $t('stats.impressions') }}
        </template>
        <template #head(clicks)>
          {{ $t('stats.clicks') }}
        </template>
        <template #head(revenue)>
          {{ $t('stats.spent') }}
        </template>
        <template #head(tax)>
          {{ $t('stats.tax') }}
        </template>
        <template #cell(date)="{ item }">
          {{ getFormattedDate(new Date(item.date)) }}
        </template>
        <template #cell(impressions)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(campaigns)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(revenue)="data">
          {{ data.item.revenue && data.item.tax ? currency('USD', data.item.revenue - data.item.tax) : (data.value ? currency('USD', data.value) : 0) }}
        </template>
        <template #cell(tax)="data">
          {{ data.value ? currency('USD', data.value) : 0 }}
        </template>
        <template #foot(date)="data">
          <strong :data-label="data.label">{{ $t('Records') }}: {{ rows.length }}</strong>
        </template>
        <template #foot(impressions)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
        </template>
        <template #foot(clicks)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('clicks')) }}
        </template>
        <template #foot(revenue)>
          {{ currency('USD', sumRevenueMinusTax()) }}
        </template>
        <template #foot(tax)>
          {{ currency('USD', sum('tax')) }}
        </template>
      </b-table>
      <div
        v-else
        class="text-center text-muted my-3"
      >
        {{ $t('No data for this period') }}
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BCard,
  BTable,
  BSpinner,
  VBTooltip,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import StatsDateRangeFilter from '@/views/dashboard/dsppartners/stats/StatsDateRangeFilter.vue'
import DspPartnerToolbar from '../DspPartnerToolbar.vue'
import StatsNavigation from './StatsNavigation.vue'

export default {
  name: 'SourceDetails',
  components: {
    BCard,
    BTable,
    BSpinner,
    BDropdown,
    BDropdownItem,
    Breadcrumbs,
    StatsNavigation,
    StatsDateRangeFilter,
    DspPartnerToolbar,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      currency: currencyFormatter,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      getFormattedDate,
      rows: [],
      sortBy: 'impressions',
      sortDesc: true,
      sortDirection: 'desc',
      fields: [
        { key: 'date', label: 'Дата', sortable: false },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'clicks', label: 'Clicks', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
        { key: 'tax', label: 'Tax', sortable: false },
      ],
      breadcrumbItems: [],
      dsppartner: {},
      sourceStatus: 'active',
      sourceId: '',
      isLoading: false,
      // Pagination
      limit: 80,
      currentPage: 1,
      totalRecords: 0,
      limitOptions: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
    }
  },
  computed: {
    selectedRangeFormated() {
      let dateFrom
      let dateTo
      const selectedRange = this.rangePicker
      if (Array.isArray(selectedRange)) {
        [dateFrom, dateTo] = selectedRange
      } else {
        [dateFrom, , dateTo] = selectedRange.split(' ')
      }

      return {
        from: getFormattedDate(new Date(dateFrom)),
        to: dateTo === undefined ? getFormattedDate(new Date(dateFrom)) : getFormattedDate(new Date(dateTo)),
      }
    },
    getDomainNameById() {
      return siteId => this.$store.getters['dashboard-dsp-domains/domainNameByIdFromStore'](siteId)
    },
  },
  created() {
    this.sourceId = this.$route.params.sourceId
    this.getPartnerData()
    this.loadDomainName()
    this.checkSourceStatus()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
      this.fetchSourceDetails() // Вызываем только один раз при инициализации
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
      this.fetchSourceDetails() // Вызываем только один раз при инициализации
    }
  },
  methods: {
    async getPartnerData() {
      const responseData = await useJwt.getDsppartner(this.$route.params.id)
      this.dsppartner = responseData.data.dsppartner || {}
      this.setBreabcrumbs()
    },
    async loadDomainName() {
      try {
        await this.$store.dispatch('dashboard-dsp-domains/fetchDomainNamesById', {
          dspId: this.$route.params.id,
          siteIds: [this.sourceId],
        })
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async checkSourceStatus() {
      try {
        const blacklistResponse = await useJwt.getDsppartnerBlacklist({
          dsp_partner_id: this.$route.params.id,
        })

        // Обрабатываем разные форматы ответа API
        let blacklistData = blacklistResponse?.data || []
        if (blacklistResponse?.data && blacklistResponse.data.data) {
          blacklistData = blacklistResponse.data.data
        } else if (blacklistResponse?.data && blacklistResponse.data.dsppartnerblacklist) {
          blacklistData = blacklistResponse.data.dsppartnerblacklist
        }

        // Проверяем, заблокирован ли текущий источник
        const isBlocked = blacklistData.some(item => item.site_id === this.sourceId)
        this.sourceStatus = isBlocked ? 'blocked' : 'active'
      } catch (error) {
        // Если ошибка при загрузке - считаем источник активным
        this.sourceStatus = 'active'
      }
    },
    openWebsite(domainName) {
      if (!domainName) return

      // Добавляем протокол если его нет
      let url = domainName
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`
      }

      // Открываем в новом окне
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'ID copied to clipboard',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'ID copied to clipboard',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      }
    },
    setBreabcrumbs() {
      const { sourceId } = this.$route.params
      this.breadcrumbItems = [
        {
          text: this.$t('DSP partners'),
          to: '/dsppartners',
        },
        {
          text: this.dsppartner.name,
          to: `/dsppartners/${this.dsppartner._id}`,
        },
        {
          text: this.$t('Statistics'),
          to: `/dsppartners/${this.dsppartner._id}/stats`,
        },
        {
          text: 'Источники',
          to: `/dsppartners/${this.dsppartner._id}/stats/sources`,
        },
        {
          text: this.getDomainNameById(sourceId),
          active: true,
        },
      ]
    },
    async fetchSourceDetails() {
      this.isLoading = true
      const { from, to } = this.selectedRangeFormated
      const { sourceId } = this.$route.params

      try {
        const response = await useJwt.getDsppartnerStatsSources(
          this.$route.params.id,
          {
            from,
            to,
            type: 'daily',
            site_id: sourceId,
            limit: this.limit,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
          },
        )
        this.rows = (response.data.items || []).map(item => ({
          ...item,
        }))
      } catch (error) {
        this.rows = []
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.isLoading = false
      }
    },
    sum(key) {
      return this.rows.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    sumRevenueMinusTax() {
      return this.rows.reduce((acc, r) => acc + ((r.revenue || 0) - (r.tax || 0)), 0)
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString()
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        // Обновляем URL без перезагрузки страницы
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.fetchSourceDetails()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async blockSource() {
      try {
        await useJwt.createDsppartnerBlacklist({
          dsp_partner_id: this.$route.params.id,
          site_id: this.sourceId,
          reason: this.$t('stats.blocked_by_admin'),
          block_date: null, // Блокируем сегодня
        })

        this.sourceStatus = 'blocked'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Source has been blocked successfully',
          },
        })
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message || 'Failed to block source',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async unblockSource() {
      try {
        // Сначала нужно найти ID блокировки
        const blacklistResponse = await useJwt.getDsppartnerBlacklist({
          dsp_partner_id: this.$route.params.id,
        })

        // Ищем нужную блокировку по site_id
        let blacklistData = blacklistResponse?.data || []
        if (blacklistResponse?.data && blacklistResponse.data.data) {
          blacklistData = blacklistResponse.data.data
        } else if (blacklistResponse?.data && blacklistResponse.data.dsppartnerblacklist) {
          blacklistData = blacklistResponse.data.dsppartnerblacklist
        }

        const blockItem = blacklistData.find(item => item.site_id === this.sourceId)

        if (blockItem) {
          await useJwt.deleteDsppartnerBlacklist(blockItem._id)

          this.sourceStatus = 'active'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'Source has been unblocked successfully',
            },
          })
        } else {
          this.$store.dispatch(
            'error-store/showNotify',
            {
              title: 'Error',
              msg: 'Block record not found',
              variant: 'danger',
            },
            { root: true },
          )
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message || 'Failed to unblock source',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    onSortChanged(ctx) {
      // Обновляем сортировку только для полей, которые поддерживаются бэкендом
      const backendSortableFields = ['impressions', 'clicks', 'revenue']
      if (backendSortableFields.includes(ctx.sortBy)) {
        this.sortBy = ctx.sortBy
        this.sortDesc = ctx.sortDesc
        this.fetchSourceDetails()
      }
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('dspStatsDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}

.copy-id {
  border-bottom: 1px dashed #ccc;
  transition: border-bottom-color 0.2s ease;
}

.copy-id:hover {
  border-bottom-color: #007bff;
}
</style>
